import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, Check, Pencil, X } from 'lucide-react'
import {
  Button,
  Input,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
} from '@atrvd/saas-ui'
import { formatCurrency } from '@dcp/shared/utils/formatters'
import { DEFAULT_DETAILS_PAGE_SIZE, MANUAL_PAGE_TRANSACTION_SOURCES } from '@/types/banking'
import { useUpdateTransactionNotes } from '@/hooks/useBanking'
// Reused as-is from the import detail modals (DCP-333) rather than re-homed to a
// shared folder or re-implemented here: it is already accessible (per-button
// aria-labels, `aria-current="page"`), already unit-tested together with
// `buildPageNumbers`, and self-hides at `totalPages <= 1` — which is exactly the
// "10 or fewer transactions render like today" requirement, for free.
import { PaginationControls } from '@/components/import/PaginationControls'

/**
 * A single bank transaction row shown in the drill-down modal.
 *
 * Rows are the real LINE OF CREDIT transactions behind a line's LOC Usage,
 * supplied by the caller from `useLocTransactions` (DCP-290).
 */
export interface BankTxn {
  /**
   * Underlying `transactions` row id — the key `updateNotes` writes against
   * when the Notes cell is edited (DCP-169).
   */
  id: string
  /**
   * Transaction `source` (e.g. 'manual', 'file_import', 'plaid', 'sage').
   * Drives the Notes-editable gate: `updateNotes` only persists for the
   * manual-page sources, so other sources stay read-only (DCP-169).
   */
  source: string
  /**
   * Facility the transaction belongs to (DCP-169). Originally documented as the
   * key a caller holding a flat transaction feed would use to scope the
   * drill-down to a Cash Position FACILITY column by id — that caller
   * (`collectFacilityTxns` in `CashPositionPage.tsx`) was deleted once scoping
   * moved server-side (DCP-333). Kept on the type regardless: cheap to carry
   * and useful when debugging which facility a row came from.
   */
  facilityId: string
  /**
   * Pack the transaction's LOC line belongs to, or null for a standalone line
   * (DCP-169). Same history as `facilityId` above — the flat-feed PACK-column
   * scoping caller (`groupPackByFacility` in `CashPositionPage.tsx`) is gone
   * now that scoping is server-side (DCP-333); the field stays for debugging.
   */
  packId: string | null
  /** Transaction date, displayed as-is (prototype uses MM.DD.YYYY). */
  date: string
  /** Bank account name, or '' if unknown (renders as "—"). */
  account: string
  /** Bank account number, or '' if unknown (renders as "—"). */
  accountNumber: string
  /** Signed transaction amount. */
  amount: number
  /** Transaction description. */
  description: string
  /** Free-form memo, or '' if unknown (renders as "—"). */
  memo: string
  /** Free-form notes, or '' if unknown (renders as "—"). */
  notes: string
}

/** A named group of transactions — one group per facility (pack rows can have several). */
export interface BankTxnGroup {
  /**
   * Stable identity of the group, used ONLY to tell two groups apart when
   * deciding heading boundaries (DCP-333, PR #184 review). `useLocTransactions`
   * always sets it to the group's `facility_id` — the key it groups by.
   *
   * WHY it exists: `facilities` carries no UNIQUE on `name`, not even per pack,
   * so one pack can legitimately hold two DIFFERENT facilities both called
   * "Katy". The hook keys its groups by facility id for exactly that reason, but
   * the modal flattens them and compares consecutive rows to find where a new
   * facility block starts — and comparing by NAME merged those two groups back
   * into a single heading, undoing the split at the render layer and breaking
   * DCP-169 AC.2's "one group per pack member".
   *
   * OPTIONAL on purpose: presentational callers and test fixtures build groups
   * by hand without a facility id, and must keep compiling. When it is absent
   * the boundary comparison falls back to `name`, i.e. today's behaviour.
   */
  id?: string
  /** Group heading shown above its transactions (e.g. the facility/reference name). */
  name: string
  transactions: BankTxn[]
}

interface ViewBankTransactionsDialogProps {
  open: boolean
  onClose: () => void
  /** Pack or facility name shown in the modal title. */
  referenceName: string
  groups: BankTxnGroup[]
  /**
   * Starting LOC usage baseline for the drilled scope (a single line's
   * starting_loc_usage_amount, or the sum across a pack's lines). LOC Usage =
   * this baseline + the transactions below, so it is shown as its own row and
   * folded into the footer Total, which then equals the LOC Usage figure the
   * user clicked from. Defaults to 0 (no baseline row rendered). (DCP-290)
   */
  startingBalance?: number
  /**
   * Whether to render the per-facility group heading above each group. True for
   * a PACK drill: the modal title is the pack name, so each block of
   * transactions needs its facility name to be attributable — including a
   * single-member pack. False for a facility / standalone drill, where the
   * modal title already names the facility and a heading would just repeat it
   * (DCP-169, PR #169 review — replaces the earlier `groups.length > 1`, which
   * silently dropped the heading for a one-member pack). Defaults to false.
   */
  showGroupHeadings?: boolean
  /**
   * Optional legacy flag that renders a "sample data" banner at the top of the
   * modal. The drill-down now shows real transactions (DCP-290), so callers no
   * longer set this; it is retained only so any future placeholder use can flag
   * itself explicitly rather than presenting fabricated rows as real data.
   */
  isSampleData?: boolean
  /**
   * Whether the transaction feed for this reference is still in flight
   * (DCP-333). The data is now fetched lazily, scoped to the drilled
   * reference, once the modal opens — so it needs its own loading state
   * rather than assuming the caller already has `groups` ready. While true,
   * the table body shows a single loading row instead of the (still empty)
   * starting-balance row and group rows, and the footer shows "Total: —"
   * instead of a figure computed from a partial feed. Defaults to false
   * (today's synchronous-`groups` behaviour, unchanged). Ignored when
   * `errorMessage` is set.
   */
  isLoading?: boolean
  /**
   * Error message from the lazy transaction fetch, if it failed (DCP-333).
   * When set, the table body shows this message instead of any rows and the
   * footer shows "Total: —"; the Close button keeps working so the user can
   * dismiss the modal. Takes precedence over `isLoading`. Defaults to
   * undefined (no error, today's behaviour).
   */
  errorMessage?: string
  /**
   * The LOC Usage figure the user drilled from, e.g. a Cash Position cell or
   * the LOC Availability page's Usage column (DCP-333). Used for ONE narrow
   * check: if this figure differs from `startingBalance` there must be
   * transactions behind it (LOC Usage = starting balance + Σ transactions), so
   * an EMPTY list is proof they were lost in transit — exactly the bug this
   * ticket fixes (an org-wide unpaginated feed truncated by PostgREST's
   * 1000-row cap rendered an empty table under a correct non-zero LOC Usage
   * cell). That case gets a warning banner above the table.
   *
   * Deliberately NOT a general `total !== expectedTotal` comparison: this
   * figure comes from the caller's CACHED page query (5-minute staleTime, no
   * refetch on focus) while the list is fetched fresh on open, so a
   * concurrently written transaction (another session, LedgerSync, Sage, a file
   * import) makes the two legitimately disagree — the list being the more
   * current of the two. Warning there would cry "transactions may be missing"
   * precisely when there are MORE of them, not fewer.
   *
   * Ignored while `isLoading` or `errorMessage` is set, since an empty list is
   * expected before the feed has settled and already explained when it failed.
   * Defaults to undefined (no check performed, today's behaviour).
   */
  expectedTotal?: number
  /**
   * Whether the most recent BACKGROUND refetch of the transaction feed failed
   * (DCP-333). Distinct from `errorMessage`: the pages only set `errorMessage`
   * when React Query has no `data` at all (`isError && !data`), because gating
   * on `isError` alone blanked a populated table for a moment right after a
   * Notes save (the mutation's invalidation briefly reports `isError` before
   * the refetch resolves). A background refetch failure under React Query v5
   * keeps the last-good `data`, so without this flag the user sees stale rows
   * with no signal that a refresh didn't happen. When true, a NON-DESTRUCTIVE
   * amber notice is shown above the table — all rows and the real Total stay
   * exactly as they were — noting that the list could not be refreshed and is
   * the last successfully loaded version. It can appear alongside the
   * missing-transactions banner (they mean different things: this is "the
   * refresh failed", that is "the data proves rows are missing"). Suppressed
   * while `isLoading` or `errorMessage` is set, since those already dominate
   * the body with a stronger signal. Defaults to false (today's behaviour).
   */
  refreshFailed?: boolean
}

/** Number of columns in the transactions table — used for the group-header row's colSpan. */
const COLUMN_COUNT = 7

/**
 * One row of the flattened, page-able transaction feed (DCP-333): the
 * transaction plus the group it came from, as a heading text and an identity.
 *
 * Pagination is FLAT across the whole ordered list rather than per group,
 * because a pack drill's groups are wildly uneven — one member facility can
 * carry 1200 rows and another 3 — and per-group paging would either give the
 * 3-row group its own pager or force a page break at every group boundary.
 * Carrying the group alongside each transaction is what lets the flat slice
 * still render DCP-169 AC.2's per-facility attribution headings.
 */
interface FlatTxnRow {
  txn: BankTxn
  /** Heading text of the group this row belongs to — display only. */
  groupName: string
  /**
   * Which group this row belongs to, as an IDENTITY rather than a label
   * (DCP-333, PR #184 review): `BankTxnGroup.id` when the caller supplies one
   * (the hook always does — it is the `facility_id` it groups by), else the
   * name. Kept separate from `groupName` because two DIFFERENT facilities of one
   * pack can share a name (no UNIQUE on `facilities.name`), and comparing labels
   * to find group boundaries collapsed them into one heading — the very case the
   * hook's id-keyed grouping exists to preserve.
   */
  groupKey: string
}

/**
 * Whether a row's Notes cell may be edited, given its transaction source
 * (DCP-169). Gated to the shared `MANUAL_PAGE_TRANSACTION_SOURCES` allow-list —
 * the exact set `db.manualTransactions.updateNotes` scopes its write to. This is
 * the UI gate; the DB scope is the backstop (a write on any other source matches
 * zero rows and errors, so the UI must not offer editing there). The constant is
 * imported from the client-free `@/types/banking`, not `db-tenant`, so this
 * presentational component stays free of the Supabase client.
 */
function isNotesEditable(source: string): boolean {
  return (MANUAL_PAGE_TRANSACTION_SOURCES as readonly string[]).includes(source)
}

/** Renders a field value, falling back to an em dash for empty strings. */
function renderOrDash(value: string): string {
  return value === '' ? '—' : value
}

/**
 * Amber notice banner shared by the sample-data placeholder banner and the
 * DCP-333 missing-transactions warning below, so both "this is not trustworthy
 * data" cases render with identical classes/icon in light and dark mode
 * instead of two copies of the same class string drifting apart over time.
 * Follows the amber missing-data warning style established in DCP-161.
 */
function AmberNotice({ children }: { children: ReactNode }) {
  return (
    <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </div>
  )
}

/**
 * "View bank transactions" drill-down modal for the Line of Credit
 * Availability page's LOC Usage column (DCP-283).
 *
 * Renders transactions grouped by facility, with a running grand total in
 * the footer. The Notes cell is inline-editable for manual / file-import rows
 * (DCP-169) — every other field, and every other source, stays read-only.
 * Notes are editable by every role (BA decision), so there is no permission
 * gate here; the source gate is the only editability rule. Edits persist
 * through {@link useUpdateTransactionNotes}; editing state is kept locally per
 * transaction id.
 *
 * Rows are paginated at {@link DEFAULT_DETAILS_PAGE_SIZE} (10) per page
 * (DCP-333) — flat across the whole ordered list, not per group. A reference
 * with 10 or fewer transactions renders exactly as before, with no pager. The
 * footer Total stays the GRAND total for the entire feed regardless of page,
 * because reconciling it against the LOC Usage figure the user drilled from is
 * the point of the modal.
 */
export function ViewBankTransactionsDialog({
  open,
  onClose,
  referenceName,
  groups,
  showGroupHeadings = false,
  startingBalance = 0,
  isSampleData = false,
  isLoading = false,
  errorMessage,
  expectedTotal,
  refreshFailed = false,
}: ViewBankTransactionsDialogProps) {
  /** Persists a Notes edit into the transaction's metadata (DCP-169). */
  const updateNotes = useUpdateTransactionNotes()

  /**
   * Per-row editing state, keyed by transaction id (mirrors the prototype's
   * `drillNoteEdits`). A key's PRESENCE means the row is in edit mode and holds
   * its draft text; absence means view mode. Keeping the draft separate from
   * the committed `txn.notes` lets Discard (AC.6) restore the original simply
   * by dropping the key — the view then falls back to `txn.notes` unchanged.
   */
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  /**
   * Transaction ids currently being saved (disables just those rows' controls).
   * A Set — not a single id — so saving two rows concurrently tracks each
   * independently; a single value would let the first row's controls re-enable
   * the moment the second Save starts, while its request is still in flight
   * (PR #169 review).
   */
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())
  /** Inline per-row save error, keyed by transaction id (raw message, no mapper). */
  const [errors, setErrors] = useState<Record<string, string>>({})
  /**
   * Notes saved during THIS modal session, keyed by transaction id (PR #169
   * review). After a successful save the row leaves edit mode and would render
   * the `txn.notes` PROP — but that only updates once the mutation's background
   * refetch of `useLocTransactions` lands, so for a moment the row would flash
   * the OLD note. Rendering `committed[id] ?? txn.notes` bridges that gap. Each
   * entry is pruned once the refetched prop catches up (effect below), so a
   * later external change to the same note isn't masked.
   */
  const [committed, setCommitted] = useState<Record<string, string>>({})

  /**
   * The 0-based page the user last NAVIGATED to in the flattened transaction
   * list (DCP-333). A target, not necessarily the page on screen: what renders
   * is `effectivePage` below, this value clamped to the feed's current length.
   * Nothing clamps this state itself, on purpose — see `effectivePage`.
   *
   * Paging is needed because the feed is now retrieved in FULL: the hook walks
   * the rows with `.range()`, so PostgREST's 1000-row cap no longer truncates
   * anything and one reference legitimately returns 1200+ rows. Rendering all of
   * them at once means ~8800 cells plus per-row inline-edit state in a single
   * modal, which is a rendering problem the correctness fix created.
   *
   * NOTE the four editing states above (`drafts`, `savingIds`, `errors`,
   * `committed`) are all keyed by TRANSACTION ID, never by index or page, so
   * slicing the rendered list cannot disturb them: an unsaved draft on a row
   * that scrolls off to page 2 is still in `drafts` under its id and reappears
   * intact when the user pages back. That is why paging needed no changes to any
   * of them — and why they must stay id-keyed.
   */
  const [page, setPage] = useState(0)

  // Drop a committed override once the incoming `groups` prop reflects it (the
  // refetch landed), so `committed` only ever bridges the save→refetch gap.
  // Walks ALL groups, deliberately not just the visible page (DCP-333): a row
  // saved on page 1 and then paged away from must still have its override pruned
  // when the refetch lands, otherwise it would mask a later external change to
  // the same note for as long as the modal stays open.
  useEffect(() => {
    setCommitted((prev) => {
      if (Object.keys(prev).length === 0) return prev
      const next = { ...prev }
      let changed = false
      for (const group of groups) {
        for (const txn of group.transactions) {
          if (txn.id in next && next[txn.id] === txn.notes) {
            delete next[txn.id]
            changed = true
          }
        }
      }
      return changed ? next : prev
    })
  }, [groups])

  /** Enter edit mode for a row, seeding the draft with its current (committed-or-prop) note. */
  const beginEdit = (txn: BankTxn) => {
    setDrafts((prev) => ({ ...prev, [txn.id]: committed[txn.id] ?? txn.notes }))
    // Clear any stale error from a previous failed attempt on this row.
    setErrors((prev) => {
      const next = { ...prev }
      delete next[txn.id]
      return next
    })
  }

  /** Update the in-flight draft text for a row. */
  const changeDraft = (id: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [id]: value }))
  }

  /** AC.6 — Discard: drop the draft (and any error) without saving; view mode restores `txn.notes`. */
  const discardEdit = (id: string) => {
    setDrafts((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  /**
   * Save a row's draft note. On success the row leaves edit mode and the
   * mutation's invalidation refetches the feed so `txn.notes` reflects the new
   * value; on failure the row stays in edit mode with the raw error shown
   * inline so the user can retry or discard.
   */
  const saveEdit = async (id: string) => {
    const saved = drafts[id] ?? ''
    setSavingIds((prev) => new Set(prev).add(id))
    try {
      await updateNotes.mutateAsync({ id, notes: saved })
      // Keep the saved text locally so the row shows it immediately, not the
      // stale prop, until the background refetch lands (pruned by the effect).
      setCommitted((prev) => ({ ...prev, [id]: saved }))
      setDrafts((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      setErrors((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [id]: error instanceof Error ? error.message : 'Failed to save note',
      }))
    } finally {
      setSavingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    }
  }

  /**
   * The whole feed as ONE ordered list, each entry tagged with its group's
   * heading name (DCP-333) — the list pagination slices.
   *
   * Order is taken verbatim from the hook (facility_name asc, transaction_date
   * desc, id asc) and must not be re-sorted here: `flatMap` over `groups` in
   * their given order preserves it, which is what makes each facility's rows
   * contiguous and therefore the group headings below meaningful.
   */
  const flat = useMemo<FlatTxnRow[]>(
    () =>
      groups.flatMap((group) =>
        group.transactions.map((txn) => ({
          txn,
          groupName: group.name,
          // `id ?? name` (DCP-333, PR #184 review): the id is what keeps two
          // same-named member facilities of one pack distinct here, and the name
          // is the documented fallback for callers/fixtures that build groups
          // without one.
          groupKey: group.id ?? group.name,
        })),
      ),
    [groups],
  )

  // Footer Total = starting baseline + EVERY transaction in the feed, so it
  // equals the LOC Usage value the user drilled from (loc_usage = starting +
  // Σ txns).
  //
  // (DCP-333) This is the GRAND total and must stay so — do NOT "fix" it to sum
  // only the current page. Reconciling this figure against the LOC Usage cell
  // the user drilled from is the entire point of the drill-down (and of the
  // `expectedTotal` guard below, which exists to catch that reconciliation
  // silently failing). A per-page total would make the modal disagree with its
  // own source figure on every page but the last of a single-page list, i.e. it
  // would look exactly like the row-loss bug this ticket fixed.
  const total = useMemo(
    () => startingBalance + flat.reduce((sum, row) => sum + row.txn.amount, 0),
    [flat, startingBalance],
  )

  /**
   * How many transaction rows the feed holds in total (DCP-333). Counted across
   * groups, NOT `groups.length`: a group carrying an empty `transactions` array
   * renders a heading and nothing else, which is still an empty list as far as
   * the reader is concerned. `flat.length` is that count by construction.
   */
  const renderedTxnCount = flat.length

  /** Total pages at 10 rows each; 0 for an empty feed, which self-hides the pager. */
  const totalPages = Math.ceil(flat.length / DEFAULT_DETAILS_PAGE_SIZE)

  /**
   * The page actually rendered: `page` clamped into the range the CURRENT feed
   * supports (DCP-333, Presto PR #184 finding 10).
   *
   * Computed synchronously during render, deliberately replacing the
   * `useEffect(…, [totalPages])` that used to call
   * `setPage(prev => Math.min(prev, totalPages - 1))`. That clamp was a
   * commit-then-effect two-step, so it landed a frame LATE: the first render
   * after `groups` shrank — a background refetch, or the invalidation a Notes
   * save triggers — still carried the OLD `page`, `flat.slice()` returned
   * nothing, and the explicit empty state did not fire because it is gated on
   * `renderedTxnCount === 0` while `flat.length` was non-zero. The user got a
   * frame of empty, unlabelled table under a non-zero Total, which is precisely
   * the row-loss symptom this modal exists to rule out. A value derived during
   * render cannot be out of step with the feed it is derived from.
   *
   * `Math.max(0, …)` keeps an emptied feed on page 0 rather than page -1.
   *
   * DELIBERATE consequence: `page` stays purely the navigation TARGET the user
   * last asked for, so it may hold a value beyond the current range while the
   * feed is temporarily short. If the feed grows back (the refetch that dropped
   * rows was itself transient), the user is returned to the page they were on
   * instead of being pinned to whatever the last page was at the shortest
   * moment — the old effect wrote the clamp into state and lost that position
   * irrecoverably.
   */
  const effectivePage = Math.min(page, Math.max(0, totalPages - 1))

  /** Index of the current page's first row in `flat` — also the continuation-heading probe. */
  const pageStart = effectivePage * DEFAULT_DETAILS_PAGE_SIZE

  /**
   * The 10 rows rendered right now. Slicing the flat list (rather than fetching a
   * page) keeps the grand total, the missing-transactions guard and the Notes
   * edit state working off the complete feed, which all three need.
   */
  const pageRows = useMemo(
    () => flat.slice(pageStart, pageStart + DEFAULT_DETAILS_PAGE_SIZE),
    [flat, pageStart],
  )

  // Back to page 1 when the user drills a DIFFERENT reference (DCP-333). The
  // modal is conditionally rendered per drill today, so it normally remounts and
  // this is redundant — but that is a caller-side detail, and a caller that
  // keeps it mounted while swapping the reference would otherwise open the next
  // facility on page 4.
  useEffect(() => {
    setPage(0)
  }, [referenceName])

  /**
   * Whether the transaction feed has SETTLED: nothing in flight and nothing
   * failed outright, so what `groups` holds is the real answer (DCP-333, Presto
   * PR #184 finding 11).
   *
   * Named rather than repeated because the three gates below —
   * `missingTransactions`, `showRefreshFailed`, `showPager` — must stay
   * IDENTICAL, not merely similar. Each of them reasons about a feed it assumes
   * is final: a warning that rows were lost, a notice that a refresh failed, and
   * controls to navigate pages. Any one of them drifting out of step would fire
   * over a placeholder body (the loading or error row replaces all rows), which
   * is a wrong claim about the data rather than a cosmetic slip.
   *
   * `errorMessage === undefined`, not truthiness: PostgREST can surface a BLANK
   * message, and reading an empty-string error as "no error" would resurrect the
   * empty table + starting-balance-only total these checks exist to catch.
   */
  const feedSettled = !isLoading && errorMessage === undefined

  /**
   * Whether the drilled LOC Usage figure PROVES transactions exist while the
   * list shows none (DCP-333) — the exact signature of the bug this ticket
   * fixes, where an org-wide unpaginated feed truncated by PostgREST's 1000-row
   * cap left a LOC line with an empty, believable-looking table under a correct
   * non-zero LOC Usage cell.
   *
   * The proof: LOC Usage = startingBalance + Σ transactions, so a figure that
   * differs from the starting balance can only be explained by transactions —
   * and an empty list then means they were lost between the aggregate and here.
   * Deliberately narrower than comparing `total` with `expectedTotal`: that
   * fires on GOOD data, because `expectedTotal` comes from the caller's cached
   * page query while the list is fetched fresh, so any concurrent write makes
   * them disagree with the LIST being right. This form cannot false-positive on
   * a line whose usage is purely its starting amount (there
   * `expectedTotal === startingBalance`), nor on a merely-stale non-zero figure
   * whose rows did arrive. Suppressed until the feed has settled, where an empty
   * list is expected or already explained. Carries `expectedTotal` narrowed to
   * `number` so the JSX below doesn't need to re-check it against `undefined`.
   */
  const missingTransactions =
    feedSettled &&
    renderedTxnCount === 0 &&
    expectedTotal !== undefined &&
    // 0.005 epsilon absorbs float rounding noise on currency math, so a figure
    // that merely rounds differently from the starting balance isn't read as
    // evidence of transactions.
    Math.abs(expectedTotal - startingBalance) > 0.005
      ? { expectedTotal }
      : null

  /**
   * Whether to show the "refresh failed" notice (DCP-333). Suppressed until the
   * feed has settled: `isLoading` and `errorMessage` both already occupy the
   * table body with a stronger, more specific signal (a spinner row, or the
   * actual error text), so stacking this on top would just be noise about the
   * same event.
   */
  const showRefreshFailed = refreshFailed && feedSettled

  /**
   * Whether to render the pager below the table (DCP-333). Suppressed until the
   * feed has settled — the body then holds a single status row, so page
   * controls would offer to navigate a list that isn't there. `totalPages > 1`
   * duplicates `PaginationControls`' own guard on purpose: without it the
   * wrapper `<div className="mt-4">` would still contribute its margin under
   * every ≤10-row table, i.e. the "no pager at all, exactly today's rendering"
   * requirement would leak a stray 16px gap. The empty state needs no separate
   * check — 0 rows means `totalPages === 0`.
   */
  const showPager = totalPages > 1 && feedSettled

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={`LOC Usage for ${referenceName}`}
      size="xl"
      // Widen past the `xl` preset (max-w-4xl) — the 7-column table plus the
      // inline Notes editor needs the extra room; `className` merges after the
      // size class on the panel, so max-w-6xl wins (DCP-169).
      className="max-w-6xl"
      footer={
        <div className="flex items-center justify-end gap-4">
          {/* (DCP-333) While loading or errored, `total` is computed from a
              feed that either hasn't arrived yet or never arrived — showing a
              figure derived only from `startingBalance` in that state would
              reproduce exactly the symptom this ticket fixes: a total that
              doesn't match what's actually listed. Show "—" instead until the
              real feed has settled. `errorMessage !== undefined`, not
              truthiness: PostgREST can surface a blank message, and an
              empty-string error must still blank the total rather than present
              a starting-balance-only figure as the answer. */}
          <span className="font-medium">
            Total: {isLoading || errorMessage !== undefined ? '—' : formatCurrency(total)}
          </span>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {/* Sample-data notice — shown while the real bank-transaction feed is
          blocked (DCP-290). Makes it unmistakable that the rows below are
          placeholders, not real financial data. */}
      {isSampleData && (
        <AmberNotice>
          Sample data — bank transactions are not yet connected. These rows are
          placeholders and do not reflect real activity.
        </AmberNotice>
      )}

      {/* Refresh-failed notice (DCP-333) — see `showRefreshFailed` above. Kept
          separate from the missing-transactions banner below (they can render
          together): this one says "the refresh didn't happen, you're looking
          at the last good load"; that one says "the data proves rows are
          gone". Non-destructive — the rows and Total below are untouched. */}
      {showRefreshFailed && (
        <AmberNotice>
          The transaction list could not be refreshed. What&apos;s shown below
          is the last successfully loaded version, which may be out of date.
        </AmberNotice>
      )}

      {/* Missing-transactions warning (DCP-333) — see the `missingTransactions`
          comment above for why this exists and why it is scoped this narrowly.
          Shown above the table, like the sample-data notice, so it's the first
          thing the user sees. Copy softened (post-review): the figure it
          compares against is a CACHED query, so the two legitimately disagree
          for a second or two during the optimistic window of a borrowing-base
          edit — a case reload resolves, unlike "contact support" which was
          never the right next step for it. */}
      {missingTransactions && (
        <AmberNotice>
          LOC Usage for {referenceName} is{' '}
          {formatCurrency(missingTransactions.expectedTotal)}, which differs from
          its starting balance of {formatCurrency(startingBalance)} — so
          transactions make up the difference, but none could be loaded here.
          This list is incomplete; please reload the page before relying on it.
        </AmberNotice>
      )}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Transaction Date</TableHead>
              <TableHead>Account Name</TableHead>
              <TableHead>Account Number</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Memo</TableHead>
              {/*
                Notes column is inline-editable for manual / file-import rows
                (DCP-169). Editing is per-row; all other columns stay read-only.
                Fixed width so the column reserves room for the edit controls
                (input + Save/Discard) even in view mode — otherwise entering
                edit mode would widen the column and make the whole table jump.
              */}
              <TableHead className="w-[280px]">Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* (DCP-333) `errorMessage` takes precedence over `isLoading`: if the
                fetch has already failed, there is nothing left in flight to wait
                on, so the error is the more accurate — and more actionable —
                thing to show. Both replace the starting-balance/group rows
                below, which would otherwise render empty (loading) or stale
                (errored) rather than reflecting the real state. Tested against
                `undefined` rather than truthiness so a blank PostgREST message
                still takes the error branch — an empty-string error read as "no
                error" would render the very same empty table under a
                starting-balance-only total that this ticket is about. */}
            {errorMessage !== undefined ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} role="alert" className="text-destructive">
                  {errorMessage}
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              <TableRow>
                {/* `role="status"` mirrors the error row's `role="alert"` above
                    (DCP-333, PR #184 review): entering the loading state
                    otherwise replaced the whole table body with text no
                    assistive tech announced, so a screen-reader user got silence
                    where a sighted user gets "Loading transactions…". `status` is
                    the polite counterpart to the error's assertive `alert` — this
                    is progress, not a problem. */}
                <TableCell colSpan={COLUMN_COUNT} role="status">
                  Loading transactions…
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* Starting LOC usage baseline (DCP-290). Rendered when non-zero so
                    the rows + this line add up to the LOC Usage in the parent table,
                    and a line with only a starting balance (no transactions) still
                    shows its figure instead of an empty modal.

                    Page 1 only (DCP-333), and it deliberately does NOT consume one
                    of the page's 10 slots: it is a baseline FIGURE, not a
                    transaction, so counting it would silently drop a transaction
                    off page 1 and shift the whole list by one. Repeating it on
                    every page was rejected for the same reason it belongs at the
                    top at all — it reads as the opening balance of the list
                    beneath it, and an "opening balance" restated above page 4's
                    rows would be arithmetic nonsense.

                    Gated on `effectivePage`, not the raw `page` state (DCP-333,
                    Presto PR #184 finding 10): once the clamp is a render-time
                    derivation, `page` can legitimately sit past the end of a
                    temporarily-short feed, and testing it here would hide the
                    baseline row from the page that IS being rendered as page 1
                    — leaving its rows not adding up to the footer Total. */}
                {effectivePage === 0 && startingBalance !== 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="font-medium">
                      Starting LOC usage balance
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-medium',
                        startingBalance < 0 && 'text-destructive',
                      )}
                    >
                      {formatCurrency(startingBalance)}
                    </TableCell>
                    <TableCell colSpan={COLUMN_COUNT - 4} />
                  </TableRow>
                )}
                {/* Explicit empty state (DCP-333). Without it, a genuinely empty
                    feed (no starting balance, no transactions) renders NOTHING
                    under "Total: $0.00" — indistinguishable from the bug this
                    ticket fixes, where rows were silently lost in transit.
                    Reachable e.g. for a standalone facility with no `loc_lines`
                    row: legitimately zero usage, zero history. A visible "there
                    really is nothing here" is what lets QA and support tell a
                    correct empty result from a broken feed without reaching for
                    a network trace. Only shown when there's also no starting
                    balance row — a starting-balance-only line already has a
                    row explaining the total, so it isn't ambiguous. */}
                {renderedTxnCount === 0 && startingBalance === 0 ? (
                  <TableRow>
                    <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground">
                      No LOC transactions for this reference.
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map(({ txn, groupName, groupKey }, index) => {
                    /**
                     * Identity of the group the PREVIOUS rendered row belongs to
                     * — the row above on this page, or (for the page's first row)
                     * the last row of the previous page, which is why `flat` is
                     * probed rather than `pageRows` there (DCP-333).
                     */
                    const previousGroupKey =
                      index === 0
                        ? pageStart > 0
                          ? flat[pageStart - 1].groupKey
                          : null
                        : pageRows[index - 1].groupKey
                    /**
                     * This row opens a new facility block (the mid-page case, as
                     * before paging).
                     *
                     * Compared by `groupKey`, NOT by the heading text (DCP-333,
                     * PR #184 review): with the name, two DIFFERENT facilities of
                     * one pack that happen to share a name — legal, `facilities`
                     * has no UNIQUE on `name` — read as one continuous group, so
                     * the second one never got its own heading and its rows
                     * appeared under the first one's. The hook groups by
                     * `facility_id` precisely to keep them apart, and this is the
                     * comparison that carries that decision into the render.
                     */
                    const startsNewGroup = groupKey !== previousGroupKey
                    /**
                     * The page begins PART-WAY THROUGH a facility's rows, i.e. the
                     * heading was printed on an earlier page. Its heading is
                     * repeated here, marked as a continuation: DCP-169 AC.2
                     * requires every block of transactions to be attributable to
                     * its member facility, and a page that opens with unlabelled
                     * rows breaks that — the user would have to page BACK to learn
                     * whose $400k draw they are looking at. The "(continued)"
                     * marker is what stops the repeat from reading as a second,
                     * separate block for the same facility.
                     */
                    const isGroupContinuation = index === 0 && !startsNewGroup
                    // Edit mode iff a draft exists for this transaction id.
                    const isEditing = txn.id in drafts
                    const isSaving = savingIds.has(txn.id)
                    // Editable when the row's source is a manual-page write source.
                    // No role gate — Notes are editable by every role (BA decision).
                    const editable = isNotesEditable(txn.source)
                    const error = errors[txn.id]
                    // Per-row aria-label so the Notes controls stay unambiguous
                    // when several rows are in edit mode at once — a bare
                    // "Edit notes" repeated across rows is ambiguous for screen
                    // readers and unstable for by-label lookups (PR #169 review).
                    const rowLabel = `${txn.date} — ${txn.description}`
                    return (
                      // Keyed by transaction id, not by index (DCP-333): with
                      // paging the index is page-local, so `page-relative index`
                      // identifies a DIFFERENT transaction on every page and
                      // React would reconcile row 1 of page 2 onto row 1 of page
                      // 1 — carrying the previous row's Notes input DOM (and its
                      // focus/selection) onto unrelated data.
                      <Fragment key={txn.id}>
                        {/* Per-facility group header — shown for a PACK drill so the
                            reader can tell which facility each block of transactions
                            belongs to (the modal title is the pack name). Driven by an
                            explicit flag, NOT `groups.length > 1`: a single-member pack
                            still needs the attribution. A facility/standalone drill
                            passes false — its title already names the facility
                            (DCP-169, PR #169 review). Emitted on the first row of a
                            group AND on the first row of every page that continues one
                            (DCP-333). */}
                        {showGroupHeadings && (startsNewGroup || isGroupContinuation) && (
                          <TableRow>
                            <TableCell
                              colSpan={COLUMN_COUNT}
                              className="bg-muted/50 font-semibold"
                            >
                              {/* Heading text stays the NAME — the identity above
                                  is for boundary detection only, never displayed
                                  (DCP-333). */}
                              {groupName}
                              {isGroupContinuation && (
                                <span className="ml-2 font-normal text-muted-foreground">
                                  (continued)
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow>
                          <TableCell>{renderOrDash(txn.date)}</TableCell>
                          <TableCell>{renderOrDash(txn.account)}</TableCell>
                          <TableCell>{renderOrDash(txn.accountNumber)}</TableCell>
                          <TableCell
                            className={cn('text-right', txn.amount < 0 && 'text-destructive')}
                          >
                            {formatCurrency(txn.amount)}
                          </TableCell>
                          <TableCell>{renderOrDash(txn.description)}</TableCell>
                          <TableCell>{renderOrDash(txn.memo)}</TableCell>
                          {/* Fixed-width Notes cell (matches the header) so the column
                              stays put when a row toggles between view and edit. */}
                          <TableCell className="w-[280px] align-top">
                            {isEditing ? (
                              // Edit mode: input + Save / Discard, disabled while saving.
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Input
                                    value={drafts[txn.id]}
                                    onChange={(e) => changeDraft(txn.id, e.target.value)}
                                    disabled={isSaving}
                                    aria-label={`Edit notes for ${rowLabel}`}
                                    // min-w-0 lets the input shrink within the fixed
                                    // cell instead of forcing the column wider.
                                    className="h-8 min-w-0"
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => void saveEdit(txn.id)}
                                    disabled={isSaving}
                                    aria-label={`Save notes for ${rowLabel}`}
                                    className="h-7 w-7 shrink-0"
                                  >
                                    <Check className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => discardEdit(txn.id)}
                                    disabled={isSaving}
                                    aria-label={`Discard notes for ${rowLabel}`}
                                    className="h-7 w-7 shrink-0"
                                  >
                                    <X className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                                {/* Raw save error, surfaced inline (no error-mapper per repo rule). */}
                                {error && (
                                  <p className="text-xs text-destructive" role="alert">
                                    {error}
                                  </p>
                                )}
                              </div>
                            ) : editable ? (
                              // View mode (editable source): note text + Edit affordance.
                              // min-h-8 matches the edit-mode input height (h-8) so
                              // toggling edit ↔ view doesn't change the row height.
                              <div className="group/note flex min-h-8 items-center gap-1">
                                <span>{renderOrDash(committed[txn.id] ?? txn.notes)}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => beginEdit(txn)}
                                  aria-label={`Edit notes for ${rowLabel}`}
                                  className="h-7 w-7 shrink-0"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              // Non-editable source (e.g. Plaid / Sage) — read-only, as before.
                              renderOrDash(txn.notes)
                            )}
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )
                  })
                )}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pager — directly BELOW the bordered table container, matching the
          import detail modals (DCP-333). Deliberately outside the Modal
          `footer`, which stays Total + Close: the footer figure is the GRAND
          total for the whole feed, so pairing it with page controls in the same
          bar would invite reading it as this page's subtotal. */}
      {showPager && (
        <div className="mt-4">
          {/* `effectivePage`, never the raw `page` state (DCP-333, Presto PR
              #184 finding 10): the pager must report and highlight the page the
              table is actually showing, and its Previous/Next arrows compute
              their target from this prop — fed a stale out-of-range `page` they
              would step relative to a page that no longer exists. */}
          <PaginationControls
            page={effectivePage}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}
    </Modal>
  )
}
